import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, not, or } from 'ember-truth-helpers';
import UnifiedScreenersModal from 'garaje/components/approvals/unified-screeners-modal';
import type { VisualComplianceReport } from 'garaje/models/screener-result';
import type VisitApproval from 'garaje/models/visit-approval';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type MetricsService from 'garaje/services/metrics';
import getModelClassName from 'garaje/utils/get-model-class-name';
import { reads } from 'macro-decorators';

export interface VisualComplianceDetailsSignature {
  Args: {
    // The context in which the component is being used
    context?: 'location' | 'property';
    reports: VisualComplianceReport[];
    visitApproval?: VisitApproval;
    model: GuestWithVisitApproval;
    hideReviewButton?: boolean;
  };

  Element: HTMLUListElement;
}

export default class VisualComplianceDetails extends Component<VisualComplianceDetailsSignature> {
  @service declare abilities: AbilitiesService;
  @service declare metrics: MetricsService;

  @tracked showVisualComplianceDetails: boolean = false;

  @reads('args.reports', []) reports!: this['args']['reports'];

  get isUnreachable(): boolean {
    const { reports } = this;

    return reports.every((r) => r['call-status'] === 'unreachable' || r['call-status'] === 'error');
  }

  get errorMessages(): string[] {
    const { reports } = this;
    return reports.map((r) => r.message).filter((msg): msg is string => Boolean(msg));
  }

  get canView(): boolean {
    const {
      abilities,
      context,
      reports: report,
      args: { model, visitApproval },
    } = this;

    if (abilities.can('review entry-approval', { context, report, model, visitApproval })) return true;
    if (abilities.can('view visual-compliance', { context, report, model, visitApproval })) return true;

    return false;
  }

  get context(): this['args']['context'] {
    return this.args.context ?? 'location';
  }

  @action
  toggleModal(value: boolean): void {
    this.showVisualComplianceDetails = value;

    if (value) {
      this.metrics.trackEvent('Unified Screener Modal Opened', {
        screenerSource: this.args.visitApproval?.visualComplianceResult?.screenerSource,
        from: `Drilled in view: ${getModelClassName(this.args.model)}`,
      });
    }
  }

  <template>
    <ul ...attributes data-test-visual-compliance-details>
      {{#if this.canView}}
        {{#if this.isUnreachable}}
          <li>
            <span class="text-papaya-50 flex" data-test-visual-compliance-unreachable>
              {{svgJar "caution-triangle" class="mr-1"}}
              Review match details unavailable
            </span>
          </li>

          {{#if this.errorMessages.length}}
            {{#each this.errorMessages as |message|}}
              <li data-test-visual-compliance-error-message>
                <span class="text-papaya-50 flex" data-test-visual-compliance-error-message>
                  {{svgJar "caution-triangle" class="mr-1"}}
                  {{message}}
                </span>
              </li>
            {{/each}}
          {{/if}}
        {{/if}}
        {{#if (and (not this.isUnreachable) (not @hideReviewButton))}}
          <li>
            <span>Review Match Details</span>
            <button
              type="button"
              class="btn btn--link btn--small !text-gem-50"
              {{on "click" (fn this.toggleModal true)}}
              data-test-visual-compliance-detail-trigger
            >
              here
            </button>
          </li>
        {{/if}}
        <li>
          <span>{{if (or this.isUnreachable @hideReviewButton) "Open" " or open"}}</span>
          <a href="https://www.visualcompliance.com" target="_blank" rel="noopener">
            Visual Compliance →
          </a>
        </li>
      {{else}}
        <li>
          Go to
          <a href="https://www.visualcompliance.com" target="_blank" rel="noopener">Visual Compliance →</a>
        </li>
      {{/if}}
    </ul>

    {{#if this.showVisualComplianceDetails}}
      {{#if @visitApproval.visualComplianceResult}}
        <UnifiedScreenersModal
          @model={{@model}}
          @focusedResult={{@visitApproval.visualComplianceResult}}
          @onClose={{fn this.toggleModal false}}
        />
      {{/if}}
    {{/if}}
  </template>
}
